import React from "react";
import { useCurrentFrame, useVideoConfig } from "remotion";
import { noise2D } from "@remotion/noise";

// Fixed column X positions
const COLUMN_XS = [50, 140, 240, 350, 470, 600, 740, 880];

// Fixed character sets per column (deterministic)
const CHAR_SETS = [
  ["0", "1", "/", "▮", "#", "$", "@"],
  ["#", "0", "1", "/", "▮", "!", "0"],
  ["▮", "/", "#", "0", "1", "▮", "/"],
  ["1", "▮", "0", "#", "/", "1", "$"],
  ["0", "#", "1", "/", "▮", "0", "@"],
  ["/", "1", "▮", "0", "#", "!", "1"],
  ["#", "▮", "/", "1", "0", "#", "▮"],
  ["1", "0", "#", "▮", "/", "$", "0"],
];

export const CyberpunkBackground: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background:
          "linear-gradient(180deg, #050010 0%, #08001a 60%, #050010 100%)",
        overflow: "hidden",
        zIndex: 1,
      }}
    >
      {/* ── Noise-driven purple haze field ── */}
      {Array.from({ length: 12 }).map((_, i) => {
        const nx = i * 0.37 + 0.1;
        // noise2D returns -1..1 — drive x/y/opacity all from noise
        const ox = noise2D("cx" + i, nx, frame * 0.004) * 320;
        const oy = noise2D("cy" + i, nx, frame * 0.003) * 400;
        const opacity = (noise2D("co" + i, nx, frame * 0.005) + 1) * 0.5 * 0.12;
        const sz = 80 + (noise2D("cs" + i, nx, frame * 0.002) + 1) * 0.5 * 100;
        const hue = i % 2 === 0 ? "123,47,255" : "0,255,229";

        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: (i / 12) * width + ox - sz / 2,
              top: (i % 4) * (height / 4) + oy - sz / 2,
              width: sz,
              height: sz,
              borderRadius: "50%",
              background: `radial-gradient(circle, rgba(${hue},${(opacity * 1.5).toFixed(3)}) 0%, transparent 70%)`,
              filter: "blur(24px)",
              pointerEvents: "none",
            }}
          />
        );
      })}

      {/* ── Watermark ── */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          fontFamily: "'Rajdhani', sans-serif",
          fontWeight: 700,
          fontSize: "280px",
          color: "rgba(123,47,255,0.04)",
          userSelect: "none",
          whiteSpace: "nowrap",
          letterSpacing: "-10px",
          zIndex: 2,
        }}
      >
        2099
      </div>

      {/* ── Noise-modulated data rain ── */}
      {COLUMN_XS.map((baseX, colIdx) => {
        // Use noise to shift each column's speed and x-position slightly
        const speedN =
          (noise2D("spd" + colIdx, colIdx * 0.5, frame * 0.001) + 1) * 0.5; // 0..1
        const period = 120 + speedN * 100; // 120–220 frames
        const progress = (frame % period) / period;
        const translateY = -140 + progress * (height + 180);
        const xDrift = noise2D("xd" + colIdx, colIdx * 0.3, frame * 0.002) * 14;

        return (
          <div
            key={colIdx}
            style={{
              position: "absolute",
              left: baseX + xDrift,
              top: 0,
              width: 18,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transform: `translateY(${translateY}px)`,
              zIndex: 3,
            }}
          >
            {CHAR_SETS[colIdx].map((char, charIdx) => {
              const glowN =
                (noise2D("gl" + colIdx, charIdx * 0.5, frame * 0.007) + 1) *
                0.5;
              const isHead = charIdx === 0;
              return (
                <span
                  key={charIdx}
                  style={{
                    fontFamily: "'Share Tech Mono', monospace",
                    fontSize: "13px",
                    color: isHead ? "#ffffff" : "#00ffe5",
                    opacity: isHead ? 0.9 : 0.1 + glowN * 0.25,
                    lineHeight: "22px",
                    display: "block",
                    textShadow: isHead ? "0 0 8px #00ffe5" : "none",
                  }}
                >
                  {char}
                </span>
              );
            })}
          </div>
        );
      })}

      {/* ── Noise-driven glitch bars (horizontal scanline tears) ── */}
      {[0.2, 0.5, 0.78].map((yFrac, i) => {
        const glitchN = noise2D("gb" + i, i * 1.7, frame * 0.06);
        const opacity = Math.max(0, glitchN) * 0.6; // only show on positive noise peaks
        const widthN = (noise2D("gw" + i, i * 2.1, frame * 0.04) + 1) * 0.5;
        const barWidth = 0.4 + widthN * 0.6; // 40–100% of width

        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: 0,
              top: yFrac * height,
              width: `${barWidth * 100}%`,
              height:
                1 + Math.floor((noise2D("gh" + i, i, frame * 0.05) + 1) * 1.5),
              background: i % 2 === 0 ? "#ff003c" : "#00ffe5",
              opacity,
              zIndex: 4,
            }}
          />
        );
      })}

      {/* ── Edge strips ── */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 3,
          height: "100%",
          background: "rgba(0,255,229,0.25)",
          zIndex: 5,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 3,
          height: "100%",
          background: "rgba(255,0,60,0.25)",
          zIndex: 5,
        }}
      />

      {/* ── Noise-driven horizontal scan grid ── */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage:
            "repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,255,229,0.015) 3px, rgba(0,255,229,0.015) 4px)",
          zIndex: 6,
          pointerEvents: "none",
        }}
      />
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
